/**
 * Cars Management Page (Work Orders)
 *
 * PURPOSE: Management surface for all cars/work orders in the system
 *
 * FEATURES:
 * - View all cars with comprehensive information
 * - Beautiful shadcn table with sorting and filtering
 * - Display: VIN, model, color, assigned line, status, current operator
 * - Filter by line, status, operator
 * - Search by VIN
 * - Export capabilities
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Filter, Loader2, Search, RefreshCw, ChevronDown, Check, ArrowUpDown, ArrowUp, ArrowDown, History, CheckSquare } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { StatusBadge } from '@/components/StatusBadge';
import { getStatusConfig } from '@/lib/status-utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { CarHistoryTimeline } from '@/components/CarHistoryTimeline';
import { useAuth } from '@/contexts/AuthContext';
import { useSiteRoles } from '@/hooks/useSiteRoles';
import { usePermissionGuard } from '@/hooks/usePermissionGuard';
import AppHeader from '@/components/AppHeader';
import { SiteSelector } from '@/components/SiteSelector';

interface Line {
  id: string;
  name: string;
  display_name: string;
  short_display_name: string;
  factory_name: string;
  area_name: string;
}

interface Car {
  id: string;
  vin: string;
  model: string | null;
  color: string | null;
  status: string;
  line_id: string | null;
  current_operator_id: string | null;
  sap_doc_num: number | null;
  item_code: string | null;
  started_at: string | null;
  completed_at: string | null;
  claimed_at: string | null;
  sap_date: string | null;
  created_at: string | null;
  current_operator_name?: string | null;
}

const CarsManagement = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { currentLineId, setCurrentLineId, currentTenantId, allLines } = useAuth();
  const { canManage, isReady: rolesReady } = useSiteRoles(currentLineId || undefined);

  // Data state
  const [cars, setCars] = useState<Car[]>([]);
  const [lines, setLines] = useState<Line[]>([]);
  const [loading, setLoading] = useState(true);

  // Filter state
  const [selectedLineId, setSelectedLineId] = useState<string>('');
  const [selectedStatus, setSelectedStatus] = useState<string>('');
  const [vinSearch, setVinSearch] = useState<string>('');

  // Sorting state
  const [sortColumn, setSortColumn] = useState<string>('sap_date');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  // History sheet state
  const [selectedCarId, setSelectedCarId] = useState<string | null>(null);
  const [selectedCarVin, setSelectedCarVin] = useState<string>('');

  // Close order dialog state
  const [showCloseDialog, setShowCloseDialog] = useState(false);
  const [selectedCar, setSelectedCar] = useState<Car | null>(null);

  // Column visibility state
  const [columnVisibility, setColumnVisibility] = useState({
    vin: true,
    model: false, // Hidden by default as requested
    itemCode: true,
    site: true,
    status: true,
    currentOperator: true,
    sapDocNum: true,
    sapDate: true,
    createdAt: false, // Hidden by default
    startedAt: true,
    completedAt: true,
  });

  /**
   * Check if user has permission to access this page
   */
  usePermissionGuard({
    enabled: rolesReady && !!currentLineId,
    allowed: canManage,
    message: t('carsManagement.errors.accessDenied'),
  });

  /**
   * Load initial data on mount
   */
  useEffect(() => {
    setLines(allLines);
    loadCars();
  }, [allLines]);

  /**
   * Reload cars when filters or sorting change
   */
  useEffect(() => {
    loadCars();
  }, [selectedLineId, selectedStatus, sortColumn, sortDirection]);

  useEffect(() => {
    if (currentLineId && currentLineId !== selectedLineId) {
      setSelectedLineId(currentLineId);
    }
  }, [currentLineId, selectedLineId]);

  const handleHeaderLineChange = (lineId?: string) => {
    const nextLineId = lineId || '';
    setCurrentLineId(lineId || null);
    setSelectedLineId(nextLineId);
  };
  /**
   * Fetch cars from database with filters and sorting
   */
  const loadCars = async () => {
    setLoading(true);
    try {
      if (!currentTenantId) {
        setCars([]);
        return;
      }

      let query = ((supabase as any)
        .schema('mod_styling'))
        .from('cars')
        .select(`
          id,
          vin,
          model,
          color,
          status,
          line_id,
          current_operator_id,
          sap_doc_num,
          item_code,
          started_at,
          completed_at,
          claimed_at,
          sap_date,
          created_at
        `)
        .eq('tenant_id', currentTenantId)
        .order(sortColumn, { ascending: sortDirection === 'asc', nullsFirst: false });

      // Apply filters
      if (selectedLineId) {
        query = query.eq('line_id', selectedLineId);
      }
      if (selectedStatus) {
        query = query.eq('status', selectedStatus);
      }

      const { data, error } = await query;

      if (error) throw error;

      const carsData = (data || []) as Car[];
      const operatorIds = Array.from(new Set(carsData
        .map(car => car.current_operator_id)
        .filter((id): id is string => !!id)));

      if (operatorIds.length > 0 && currentTenantId) {
        const { data: profiles } = await ((supabase as any)
          .schema('mod_styling'))
          .from('v_profiles')
          .select('id, full_name')
          .in('id', operatorIds)
          .eq('tenant_id', currentTenantId);

        const profileMap = new Map<string, string | null>(
          (profiles || []).map((profile: any) => [profile.id, profile.full_name || null])
        );
        setCars(carsData.map(car => ({
          ...car,
          current_operator_name: car.current_operator_id
            ? profileMap.get(car.current_operator_id) ?? null
            : null,
        })));
      } else {
        setCars(carsData);
      }
    } catch (error) {
      console.error('Error loading cars:', error);
      toast.error(t('carsManagement.errors.loadCars'));
    } finally {
      setLoading(false);
    }
  };

  /**
   * Clear all filters
   */
  const handleClearFilters = () => {
    setSelectedLineId('');
    setSelectedStatus('');
    setVinSearch('');
  };

  /**
   * Handle column sorting
   */
  const handleSort = (column: string) => {
    if (sortColumn === column) {
      // Toggle direction if clicking the same column
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new column with default descending direction
      setSortColumn(column);
      setSortDirection('desc');
    }
  };

  const canCloseOrders = canManage;

  /**
   * Format date string
   */
  const formatDate = (dateString: string | null): string => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('cs-CZ', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return '-';
    }
  };

  const getLineLabel = (lineId?: string | null) => {
    if (!lineId) return '-';
    const line = lines.find(item => item.id === lineId);
    return line?.name || '-';
  };

  // Filter cars by VIN search
  const filteredCars = cars.filter(car => {
    if (!vinSearch) return true;
    return car.vin.toLowerCase().includes(vinSearch.toLowerCase());
  });

  const hasActiveFilters = selectedLineId !== '' || selectedStatus !== '' || vinSearch !== '';

  /**
   * Render sort icon for column header
   */
  const getSortIcon = (column: string) => {
    if (sortColumn !== column) {
      return <ArrowUpDown className="ml-2 h-4 w-4 opacity-50" />;
    }
    return sortDirection === 'asc'
      ? <ArrowUp className="ml-2 h-4 w-4 text-primary" />
      : <ArrowDown className="ml-2 h-4 w-4 text-primary" />;
  };

  // Column visibility helpers
  const toggleColumn = (column: keyof typeof columnVisibility) => {
    setColumnVisibility(prev => ({
      ...prev,
      [column]: !prev[column]
    }));
  };

  const visibleColumnsCount = Object.values(columnVisibility).filter(Boolean).length;
  const totalColumnsCount = Object.keys(columnVisibility).length;

  const columnLabels = {
    vin: t('carsManagement.tableHeaders.vin'),
    model: t('carsManagement.tableHeaders.model'),
    itemCode: t('carsManagement.tableHeaders.itemCode'),
    site: t('carsManagement.tableHeaders.site'),
    status: t('carsManagement.tableHeaders.status'),
    currentOperator: t('carsManagement.tableHeaders.currentOperator'),
    sapDocNum: t('carsManagement.tableHeaders.sapDocNum'),
    sapDate: t('carsManagement.tableHeaders.sapDate'),
    createdAt: t('carsManagement.tableHeaders.createdAt'),
    startedAt: t('carsManagement.tableHeaders.startedAt'),
    completedAt: t('carsManagement.tableHeaders.completedAt'),
  };

  return (
    <div className="min-h-screen bg-background">
      <AppHeader
        backTo="/"
        backLabel={t('back')}
        title={t('carsManagement.title')}
        subtitle={t('carsManagement.subtitle')}
        rightContent={(
          <SiteSelector
            selectedSiteId={currentLineId || undefined}
            onSiteChange={handleHeaderLineChange}
            showWhenSingle
          />
        )}
        action={(
          <Button onClick={loadCars} variant="outline" size="sm" className="gap-2">
            <RefreshCw className="h-4 w-4" />
            {t('carsManagement.refresh')}
          </Button>
        )}
      />

      <main className="p-4 md:p-6 lg:p-8">
        <div className="max-w-[1800px] mx-auto space-y-6">

        {loading && (
          <div className="flex items-center gap-3 px-4 py-3 rounded-lg border border-border/60 bg-muted/60 shadow-sm">
            <Loader2 className="h-5 w-5 text-primary animate-spin" />
            <div className="space-y-0.5">
              <p className="text-sm font-semibold text-foreground">
                {t('carsManagement.loadingTitle')}
              </p>
              <p className="text-xs text-muted-foreground">
                {t('carsManagement.loadingSubtitle')}
              </p>
            </div>
          </div>
        )}

        {/* Filters Card */}
        <Card className="shadow-sm border-border/50">
          <CardHeader className="pb-4">
            <div className="flex items-center gap-2">
              <div className="p-2 rounded-lg bg-primary/10">
                <Filter className="h-4 w-4 text-primary" />
              </div>
              <CardTitle className="text-lg font-semibold">
                {t('carsManagement.filters')}
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* VIN Search */}
              <div className="space-y-2">
                <Label htmlFor="vin-search" className="text-sm font-medium">
                  {t('carsManagement.vinSearch')}
                </Label>
                <div className="flex gap-2">
                  <Input
                    id="vin-search"
                    placeholder={t('carsManagement.vinSearchPlaceholder')}
                    value={vinSearch}
                    onChange={(e) => setVinSearch(e.target.value)}
                    className="flex-1"
                  />
                  <Button
                    variant="outline"
                    size="icon"
                    disabled={!vinSearch}
                    className="shrink-0"
                  >
                    <Search className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              {/* Line Filter */}
              <div className="space-y-2">
                <Label htmlFor="line-filter" className="text-sm font-medium">
                  {t('carsManagement.siteFilter')}
                </Label>
                <Select
                  value={selectedLineId || 'ALL_LINES'}
                  onValueChange={(value) => {
                    if (value === 'ALL_LINES') {
                      setSelectedLineId('');
                    } else {
                      setSelectedLineId(value);
                    }
                  }}
                >
                  <SelectTrigger id="line-filter">
                    <SelectValue placeholder={t('carsManagement.allSites')}>
                      {selectedLineId
                        ? lines.find(line => line.id === selectedLineId)?.name || t('carsManagement.allSites')
                        : t('carsManagement.allSites')}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ALL_LINES">{t('carsManagement.allSites')}</SelectItem>
                    {lines.map((line) => (
                      <SelectItem key={line.id} value={line.id}>
                        <div className="flex flex-col">
                          <span className="font-medium">{line.name}</span>
                          <span className="text-xs text-muted-foreground">{`${line.factory_name} / ${line.area_name}`}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Status Filter */}
              <div className="space-y-2">
                <Label htmlFor="status-filter" className="text-sm font-medium">
                  {t('carsManagement.statusFilter')}
                </Label>
                <Select
                  value={selectedStatus || 'ALL_STATUS'}
                  onValueChange={(value) => {
                    if (value === 'ALL_STATUS') {
                      setSelectedStatus('');
                    } else {
                      setSelectedStatus(value);
                    }
                  }}
                >
                  <SelectTrigger id="status-filter">
                    <SelectValue placeholder={t('carsManagement.allStatuses')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ALL_STATUS">{t('carsManagement.allStatuses')}</SelectItem>
                    <SelectItem value="pending">{t('carsManagement.statusOptions.pending')}</SelectItem>
                    <SelectItem value="in_progress">{t('carsManagement.statusOptions.inProgress')}</SelectItem>
                    <SelectItem value="completed">{t('carsManagement.statusOptions.completed')}</SelectItem>
                    <SelectItem value="quality_check">{t('carsManagement.statusOptions.qualityCheck')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Clear Filters Button */}
              {hasActiveFilters && (
                <div className="flex items-end">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleClearFilters}
                    className="text-xs text-muted-foreground hover:text-destructive"
                  >
                    {t('carsManagement.clearFilters')}
                  </Button>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Data Summary Bar */}
        <div className="flex items-center justify-between px-1">
          <div className="flex items-center gap-2 text-sm">
            <span className="text-muted-foreground">{t('carsManagement.showing')}</span>
            <span className="font-semibold text-primary text-lg">
              {filteredCars.length}
            </span>
            <span className="text-muted-foreground">{t('carsManagement.vehicle', { count: filteredCars.length })}</span>
          </div>
        </div>

        {/* Table Card */}
        <Card className="shadow-sm border-border/50">
          <CardContent className="p-6">
            {/* Column Visibility Toggle */}
            <div className="flex items-center justify-between mb-4">
              <div className="text-sm text-muted-foreground">
                {t('carsManagement.columnsVisible', { count: visibleColumnsCount, total: totalColumnsCount })}
              </div>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="gap-2">
                    {t('carsManagement.columns')}
                    <ChevronDown className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56 max-h-[min(400px,80vh)] overflow-y-auto">
                  {(Object.keys(columnVisibility) as Array<keyof typeof columnVisibility>).map((column) => {
                    const isVisible = columnVisibility[column];
                    return (
                      <div
                        key={column}
                        className="relative flex cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none transition-colors hover:bg-accent hover:text-accent-foreground"
                        onClick={() => toggleColumn(column)}
                      >
                        <div className="w-4 h-4 mr-2 flex items-center justify-center">
                          {isVisible && <Check className="h-4 w-4 text-primary" />}
                        </div>
                        <span>{columnLabels[column]}</span>
                      </div>
                    );
                  })}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            <div className="rounded-md border bg-card shadow-sm overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    {columnVisibility.vin && (
                      <TableHead>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 font-semibold hover:bg-transparent p-0"
                          onClick={() => handleSort('vin')}
                        >
                          {t('carsManagement.tableHeaders.vin')}
                          {getSortIcon('vin')}
                        </Button>
                      </TableHead>
                    )}
                    {columnVisibility.model && (
                      <TableHead>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 font-semibold hover:bg-transparent p-0"
                          onClick={() => handleSort('model')}
                        >
                          {t('carsManagement.tableHeaders.model')}
                          {getSortIcon('model')}
                        </Button>
                      </TableHead>
                    )}
                    {columnVisibility.itemCode && (
                      <TableHead>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 font-semibold hover:bg-transparent p-0"
                          onClick={() => handleSort('item_code')}
                        >
                          {t('carsManagement.tableHeaders.itemCode')}
                          {getSortIcon('item_code')}
                        </Button>
                      </TableHead>
                    )}
                    {columnVisibility.site && <TableHead className="font-semibold">{t('carsManagement.tableHeaders.site')}</TableHead>}
                    {columnVisibility.status && (
                      <TableHead>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 font-semibold hover:bg-transparent p-0"
                          onClick={() => handleSort('status')}
                        >
                          {t('carsManagement.tableHeaders.status')}
                          {getSortIcon('status')}
                        </Button>
                      </TableHead>
                    )}
                    {columnVisibility.currentOperator && <TableHead className="font-semibold">{t('carsManagement.tableHeaders.currentOperator')}</TableHead>}
                    {columnVisibility.sapDocNum && (
                      <TableHead>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 font-semibold hover:bg-transparent p-0"
                          onClick={() => handleSort('sap_doc_num')}
                        >
                          {t('carsManagement.tableHeaders.sapDocNum')}
                          {getSortIcon('sap_doc_num')}
                        </Button>
                      </TableHead>
                    )}
                    {columnVisibility.sapDate && (
                      <TableHead>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 font-semibold hover:bg-transparent p-0"
                          onClick={() => handleSort('sap_date')}
                        >
                          {t('carsManagement.tableHeaders.sapDate')}
                          {getSortIcon('sap_date')}
                        </Button>
                      </TableHead>
                    )}
                    {columnVisibility.createdAt && (
                      <TableHead>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 font-semibold hover:bg-transparent p-0"
                          onClick={() => handleSort('created_at')}
                        >
                          {t('carsManagement.tableHeaders.createdAt')}
                          {getSortIcon('created_at')}
                        </Button>
                      </TableHead>
                    )}
                    {columnVisibility.startedAt && (
                      <TableHead>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 font-semibold hover:bg-transparent p-0"
                          onClick={() => handleSort('started_at')}
                        >
                          {t('carsManagement.tableHeaders.startedAt')}
                          {getSortIcon('started_at')}
                        </Button>
                      </TableHead>
                    )}
                    {columnVisibility.completedAt && (
                      <TableHead>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 font-semibold hover:bg-transparent p-0"
                          onClick={() => handleSort('completed_at')}
                        >
                          {t('carsManagement.tableHeaders.completedAt')}
                          {getSortIcon('completed_at')}
                        </Button>
                      </TableHead>
                    )}
                    <TableHead className="w-[100px] font-semibold">{t('carsManagement.tableHeaders.actions')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={visibleColumnsCount} className="h-24 text-center">
                        <div className="flex items-center justify-center gap-3">
                          <Loader2 className="h-5 w-5 text-primary animate-spin" />
                          <span className="text-muted-foreground">{t('common.loading')}</span>
                        </div>
                      </TableCell>
                    </TableRow>
                  ) : filteredCars.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={visibleColumnsCount} className="h-32 text-center">
                        <div className="flex flex-col items-center justify-center text-muted-foreground">
                          <p className="text-lg font-medium">{t('carsManagement.noWorkOrders')}</p>
                          <p className="text-sm">{t('carsManagement.noWorkOrdersDesc')}</p>
                        </div>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredCars.map((car) => (
                      <TableRow
                        key={car.id}
                        className="hover:bg-muted/50 transition-colors"
                      >
                        {columnVisibility.vin && (
                          <TableCell className="font-mono font-semibold">
                            {car.vin}
                          </TableCell>
                        )}
                        {columnVisibility.model && (
                          <TableCell className="text-sm">
                            {car.model || '-'}
                          </TableCell>
                        )}
                        {columnVisibility.itemCode && (
                          <TableCell className="font-mono text-xs">
                            {car.item_code || '-'}
                          </TableCell>
                        )}
                        {columnVisibility.site && (
                          <TableCell>
                            <span className="text-sm text-muted-foreground">
                              {getLineLabel(car.line_id)}
                            </span>
                          </TableCell>
                        )}
                        {columnVisibility.status && (
                          <TableCell>
                            <StatusBadge status={car.status} />
                          </TableCell>
                        )}
                        {columnVisibility.currentOperator && (
                          <TableCell className="text-sm">
                            {car.current_operator_name || '-'}
                          </TableCell>
                        )}
                        {columnVisibility.sapDocNum && (
                          <TableCell className="font-mono text-sm">
                            {car.sap_doc_num || '-'}
                          </TableCell>
                        )}
                        {columnVisibility.sapDate && (
                          <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                            {formatDate(car.sap_date)}
                          </TableCell>
                        )}
                        {columnVisibility.createdAt && (
                          <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                            {formatDate(car.created_at)}
                          </TableCell>
                        )}
                        {columnVisibility.startedAt && (
                          <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                            {formatDate(car.started_at)}
                          </TableCell>
                        )}
                        {columnVisibility.completedAt && (
                          <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                            {formatDate(car.completed_at)}
                          </TableCell>
                        )}
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setSelectedCarId(car.id);
                                setSelectedCarVin(car.vin);
                              }}
                              title={t('carsManagement.viewHistory')}
                            >
                              <History className="h-4 w-4" />
                            </Button>
                            {canCloseOrders && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  setSelectedCar(car);
                                  setShowCloseDialog(true);
                                }}
                                className="text-primary hover:text-primary hover:bg-primary/10"
                                title={t('arrivals.closeOrder')}
                              >
                                <CheckSquare className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </div>
    </main>

      {/* History Sheet */}
      <Sheet open={!!selectedCarId} onOpenChange={(open) => !open && setSelectedCarId(null)}>
        <SheetContent className="w-full sm:max-w-lg flex flex-col">
          <SheetHeader className="flex-none">
            <SheetTitle>{t('carsManagement.vehicleHistory')}</SheetTitle>
            <SheetDescription className="font-mono">{selectedCarVin}</SheetDescription>
          </SheetHeader>
          <div className="flex-1 mt-6 overflow-hidden">
            {selectedCarId && <CarHistoryTimeline carId={selectedCarId} />}
          </div>
        </SheetContent>
      </Sheet>

      {/* Close Order Dialog */}
      {selectedCar && (
        <AlertDialog open={showCloseDialog} onOpenChange={setShowCloseDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2">
                <CheckSquare className="h-5 w-5 text-primary" />
                {t('carsManagement.closeOrderDialog.title')}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {t('carsManagement.closeOrderDialog.description')}
                <br /><br />
                <strong>{t('common.vin')}:</strong> {selectedCar.vin}
                <br />
                <strong>{t('carsManagement.tableHeaders.sapDocNum')}:</strong> {selectedCar.sap_doc_num || '-'}
                <br />
                <strong>{t('carsManagement.tableHeaders.status')}:</strong> {t(getStatusConfig(selectedCar.status).labelKey)}
                <br />
                <strong>{t('carsManagement.tableHeaders.site')}:</strong> {getLineLabel(selectedCar.line_id)}
                <br /><br />
                {t('carsManagement.closeOrderDialog.warning')}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{t('carsManagement.closeOrderDialog.cancel')}</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => {
                  setShowCloseDialog(false);
                  setSelectedCar(null);
                  toast.info(t('carsManagement.sapIntegrationPending.title'), {
                    description: t('carsManagement.sapIntegrationPending.description'),
                  });
                }}
                className="bg-primary text-primary-foreground hover:bg-primary/90"
              >
                {t('carsManagement.closeOrderDialog.confirm')}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </div>
  );
};

export default CarsManagement;
